import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { SettingsSectionForm } from '@/components/SettingsSectionForm'
import {
	buildTenantSettingsInitialValues,
	getTenantSettingsSectionConfig,
	getTenantSettingsSectionKeyFromSlug,
	type TenantSettingsSectionKey,
	tenantSettingsLabelByKey,
	tenantSettingsSlugByKey,
	tenantSettingsTabs,
} from '@/entity-config-runtime/entity-settings'
import { usePauseReasonOptionsQuery } from '@/hooks/use-settings'
import {
	useSaveTenantSettingsSectionMutation,
	useTenantSettingsSectionQuery,
} from '@/hooks/use-tenant-settings'
import { useAppAbility } from '@/integrations/casl/ability'
import {
	nav_settings,
	settings_globalInfo,
} from '@/integrations/paraglide/messages/_index.js'

export const Route = createFileRoute('/_authenticated/settings/$section')({
	component: TenantSettingsSectionRoute,
	staticData: {
		breadcrumb: ({ params }) => {
			const sectionKey = getTenantSettingsSectionKeyFromSlug(
				String(params.section ?? ''),
			)
			if (!sectionKey) {
				return { label: nav_settings() }
			}
			return { label: tenantSettingsLabelByKey[sectionKey] }
		},
	},
})

function TenantSettingsSectionRoute() {
	const { section } = Route.useParams()
	const navigate = Route.useNavigate()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const ability = useAppAbility()
	const canModifySettings = ability.can('modify', 'settings')

	const sectionKey = getTenantSettingsSectionKeyFromSlug(String(section))
	const activeSection = sectionKey ?? 'generalSettings'
	const settingsSectionQuery = useTenantSettingsSectionQuery(activeSection)
	const pauseReasonOptionsQuery = usePauseReasonOptionsQuery()

	const initialValues = useMemo(
		() => buildTenantSettingsInitialValues(settingsSectionQuery.data),
		[settingsSectionQuery.data],
	)

	const saveMutation = useSaveTenantSettingsSectionMutation(activeSection)

	const getSectionConfig = (key: TenantSettingsSectionKey) => {
		const sectionConfig = getTenantSettingsSectionConfig(key)
		if (key !== 'loginSettings') {
			return sectionConfig
		}

		return {
			...sectionConfig,
			fields: sectionConfig.fields.map((field) => {
				if (field.name !== 'loginSettings.defaultPauseReasonId') {
					return field
				}

				const disabledEvaluator =
					typeof field.disabled === 'function' ? field.disabled : undefined

				const disabled = disabledEvaluator
					? (values: Record<string, unknown>) =>
							disabledEvaluator(values) || pauseReasonOptionsQuery.isLoading
					: Boolean(field.disabled) || pauseReasonOptionsQuery.isLoading

				return {
					...field,
					type: 'select' as const,
					options: pauseReasonOptionsQuery.data ?? [],
					disabled,
				}
			}),
		}
	}

	if (!sectionKey) {
		return (
			<Typography color="text.secondary">Unknown settings section.</Typography>
		)
	}

	const renderSectionForm = (key: TenantSettingsSectionKey) => {
		if (settingsSectionQuery.isError) {
			return (
				<Typography color="error">Failed to load tenant settings.</Typography>
			)
		}

		if (settingsSectionQuery.isLoading) {
			return <Typography color="text.secondary">Loading settings...</Typography>
		}

		return (
			<SettingsSectionForm
				key={`${key}-${JSON.stringify(initialValues)}`}
				section={getSectionConfig(key)}
				initialValues={initialValues}
				onSubmit={async (values) => {
					if (!canModifySettings) {
						return
					}
					await saveMutation.mutateAsync(values)
				}}
				isSaving={saveMutation.isPending}
				showReset={canModifySettings}
				isReadOnly={!canModifySettings}
			/>
		)
	}

	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			}}
		>
			{!isMobile && (
				<Tabs
					value={sectionKey}
					onChange={(_, value) => {
						void navigate({
							to: '/settings/$section',
							params: {
								section:
									tenantSettingsSlugByKey[
										value as keyof typeof tenantSettingsSlugByKey
									],
							},
						})
					}}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ mb: 2 }}
				>
					{tenantSettingsTabs.map((tab) => (
						<Tab key={tab.key} value={tab.key} label={tab.label} />
					))}
				</Tabs>
			)}

			<Box
				sx={{
					mb: 3,
					px: 2.25,
					py: 1.5,
					borderRadius: 2,
					border: '1px solid',
					borderColor: 'divider',
					backgroundColor: 'background.paper',
					color: 'text.secondary',
					boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
					maxWidth: 640,
				}}
			>
				<Typography variant="body2" sx={{ lineHeight: 1.6 }}>
					{settings_globalInfo()}
				</Typography>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pr: 1, pb: 2 }}>
				{isMobile
					? tenantSettingsTabs.map((tab) => (
							<Accordion
								key={tab.key}
								expanded={sectionKey === tab.key}
								onChange={(_, expanded) => {
									if (expanded) {
										void navigate({
											to: '/settings/$section',
											params: { section: tab.slug },
											replace: true,
										})
									}
								}}
								disableGutters
							>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography fontWeight={600}>{tab.label}</Typography>
								</AccordionSummary>
								<AccordionDetails sx={{ px: 0 }}>
									{sectionKey === tab.key && renderSectionForm(tab.key)}
								</AccordionDetails>
							</Accordion>
						))
					: renderSectionForm(sectionKey)}
			</Box>
		</Box>
	)
}
